import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ListChecks, Tag, Images, FileText, Building2, Inbox,
  Plus, Pencil, Check, Upload, Star, Phone, Clock, MapPin,
  Send, ArrowUpRight, GripVertical,
} from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { BrowserFrame } from "@/components/mockups/BrowserFrame";
import { cn } from "@/lib/utils";

const TABS = [
  { id: "services", label: "Services", icon: ListChecks },
  { id: "pricing", label: "Pricing", icon: Tag },
  { id: "photos", label: "Photos", icon: Images },
  { id: "about", label: "About Us", icon: FileText },
  { id: "info", label: "Business Info", icon: Building2 },
  { id: "request", label: "Request Service", icon: Inbox },
];

const ease = [0.22, 1, 0.36, 1];
const dashMotion = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
  transition: { duration: 0.35, ease },
};
const siteMotion = dashMotion;

/* ---------------- Dashboard (left, navy) panels ---------------- */
const DashHeader = ({ title, action }) => (
  <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
    <div>
      <div className="text-[11px] uppercase tracking-wider text-navy-muted">Manage</div>
      <div className="font-display text-sm font-semibold text-navy-foreground">{title}</div>
    </div>
    <span className="inline-flex items-center gap-1.5 rounded-full bg-gradient-blue px-2.5 py-1 text-[10px] font-semibold text-primary-foreground">
      <Plus className="h-3 w-3" /> {action}
    </span>
  </div>
);

const Row = ({ children, dim }) => (
  <div
    className={cn(
      "flex items-center gap-3 rounded-lg border border-white/10 bg-white/5 px-3 py-2.5",
      dim && "opacity-60"
    )}
  >
    {children}
  </div>
);

const DashServices = () => (
  <div>
    <DashHeader title="Services" action="Add service" />
    <div className="space-y-2 p-4">
      {["Deep Home Cleaning", "Move-Out Cleaning", "Office & Commercial", "Carpet & Upholstery"].map(
        (s, i) => (
          <Row key={s} dim={i === 3}>
            <GripVertical className="h-3.5 w-3.5 text-navy-muted" />
            <div className="flex-1">
              <div className="text-[12px] font-medium text-navy-foreground">{s}</div>
              <div className="text-[10px] text-navy-muted">{i === 3 ? "Draft" : "Published"}</div>
            </div>
            <span className="grid h-6 w-6 place-items-center rounded-md bg-white/10 text-techblue-glow">
              <Pencil className="h-3 w-3" />
            </span>
          </Row>
        )
      )}
    </div>
  </div>
);

const DashPricing = () => (
  <div>
    <DashHeader title="Pricing" action="Add plan" />
    <div className="space-y-2 p-4">
      {[
        ["Standard Clean", "$129"],
        ["Deep Clean", "$219"],
        ["Move-Out", "$299"],
      ].map(([name, price]) => (
        <Row key={name}>
          <div className="flex-1 text-[12px] font-medium text-navy-foreground">{name}</div>
          <div className="flex items-center gap-1.5 rounded-md bg-white/10 px-2 py-1">
            <span className="text-[12px] font-bold text-techblue-glow">{price}</span>
            <Pencil className="h-3 w-3 text-navy-muted" />
          </div>
        </Row>
      ))}
      <div className="rounded-lg border border-dashed border-white/20 px-3 py-2 text-center text-[10px] text-navy-muted">
        Changes publish instantly
      </div>
    </div>
  </div>
);

const DashPhotos = () => (
  <div>
    <DashHeader title="Photos" action="Upload" />
    <div className="p-4">
      <div className="mb-3 flex items-center justify-center gap-2 rounded-lg border border-dashed border-white/20 bg-white/5 py-3 text-[11px] text-navy-muted">
        <Upload className="h-3.5 w-3.5 text-techblue-glow" /> Drag & drop images
      </div>
      <div className="grid grid-cols-3 gap-2">
        {Array.from({ length: 6 }).map((_, i) => (
          <div
            key={i}
            className="aspect-square rounded-lg bg-gradient-to-br from-white/15 to-white/5 ring-1 ring-white/10"
          />
        ))}
      </div>
    </div>
  </div>
);

const DashAbout = () => (
  <div>
    <DashHeader title="About Us" action="Edit" />
    <div className="space-y-2.5 p-4">
      <div className="rounded-lg border border-white/10 bg-white/5 p-3">
        <div className="text-[10px] uppercase tracking-wider text-navy-muted">Headline</div>
        <div className="mt-1 text-[12px] font-medium text-navy-foreground">
          Family-owned. Trusted since 2009.
        </div>
      </div>
      <div className="rounded-lg border border-white/10 bg-white/5 p-3">
        <div className="text-[10px] uppercase tracking-wider text-navy-muted">Story</div>
        <div className="mt-1.5 space-y-1.5">
          <div className="h-1.5 w-full rounded bg-white/25" />
          <div className="h-1.5 w-11/12 rounded bg-white/25" />
          <div className="h-1.5 w-2/3 rounded bg-white/25" />
        </div>
      </div>
    </div>
  </div>
);

const DashInfo = () => (
  <div>
    <DashHeader title="Business Info" action="Save" />
    <div className="space-y-2 p-4">
      {[
        [Phone, "Phone", "(209) 555-0142"],
        [Clock, "Hours", "Mon–Sat · 8AM–6PM"],
        [MapPin, "Service Area", "Central Valley, CA"],
      ].map(([Icon, label, val]) => (
        <Row key={label}>
          <span className="grid h-7 w-7 place-items-center rounded-md bg-white/10 text-techblue-glow">
            <Icon className="h-3.5 w-3.5" />
          </span>
          <div className="flex-1">
            <div className="text-[10px] text-navy-muted">{label}</div>
            <div className="text-[12px] font-medium text-navy-foreground">{val}</div>
          </div>
          <Pencil className="h-3 w-3 text-navy-muted" />
        </Row>
      ))}
    </div>
  </div>
);

const DashRequest = () => (
  <div>
    <DashHeader title="Service Requests" action="Export" />
    <div className="space-y-2 p-4">
      {[
        ["Maria G.", "Deep Clean", "New"],
        ["Anter Co.", "Office Clean", "New"],
        ["Devon P.", "Move-Out", "Replied"],
      ].map(([name, type, status]) => (
        <Row key={name}>
          <span className="grid h-7 w-7 place-items-center rounded-full bg-gradient-blue text-[10px] font-bold text-primary-foreground">
            {name[0]}
          </span>
          <div className="flex-1">
            <div className="text-[12px] font-medium text-navy-foreground">{name}</div>
            <div className="text-[10px] text-navy-muted">{type}</div>
          </div>
          <span
            className={cn(
              "rounded-full px-2 py-0.5 text-[9px] font-semibold",
              status === "New"
                ? "bg-techblue-glow/20 text-techblue-glow"
                : "bg-white/10 text-navy-muted"
            )}
          >
            {status}
          </span>
        </Row>
      ))}
    </div>
  </div>
);

/* ---------------- Website (right, light) panels ---------------- */
const SiteChrome = ({ children }) => (
  <div className="min-h-[300px] bg-gradient-subtle">
    <div className="flex items-center justify-between border-b border-border bg-card px-4 py-3">
      <div className="flex items-center gap-2">
        <div className="grid h-6 w-6 place-items-center rounded-md bg-gradient-navy">
          <Star className="h-3 w-3 text-navy-foreground" />
        </div>
        <span className="font-display text-[13px] font-bold text-navy">SparkleClean Co.</span>
      </div>
      <div className="hidden gap-3 text-[10px] font-medium text-muted-foreground sm:flex">
        <span>Services</span>
        <span>Pricing</span>
        <span className="text-primary">Contact</span>
      </div>
    </div>
    <div className="p-4">{children}</div>
  </div>
);

const SiteServices = () => (
  <div>
    <div className="text-[10px] font-semibold uppercase tracking-wider text-primary">Our Services</div>
    <h4 className="font-display text-lg font-bold text-navy">Spotless spaces, every time</h4>
    <div className="mt-3 grid grid-cols-2 gap-2.5">
      {["Deep Home Cleaning", "Move-Out Cleaning", "Office & Commercial", "Carpet & Upholstery"].map(
        (s) => (
          <div key={s} className="rounded-xl border border-border bg-card p-3 shadow-sm">
            <div className="mb-2 grid h-8 w-8 place-items-center rounded-lg bg-accent">
              <Check className="h-4 w-4 text-primary" />
            </div>
            <div className="text-[11px] font-semibold text-navy">{s}</div>
            <div className="mt-1 text-[9px] text-muted-foreground">Trusted pros · insured</div>
          </div>
        )
      )}
    </div>
  </div>
);

const SitePricing = () => (
  <div>
    <div className="text-[10px] font-semibold uppercase tracking-wider text-primary">Pricing</div>
    <h4 className="font-display text-lg font-bold text-navy">Simple, honest rates</h4>
    <div className="mt-3 grid grid-cols-3 gap-2.5">
      {[
        ["Standard", "$129", false],
        ["Deep", "$219", true],
        ["Move-Out", "$299", false],
      ].map(([name, price, hot]) => (
        <div
          key={name}
          className={cn(
            "rounded-xl border p-3 text-center shadow-sm",
            hot ? "border-transparent bg-gradient-navy" : "border-border bg-card"
          )}
        >
          <div className={cn("text-[10px] font-medium", hot ? "text-navy-muted" : "text-muted-foreground")}>
            {name}
          </div>
          <div className={cn("mt-1 font-display text-lg font-bold", hot ? "text-navy-foreground" : "text-navy")}>
            {price}
          </div>
          <div
            className={cn(
              "mt-2 rounded-full py-1 text-[9px] font-semibold",
              hot ? "bg-gradient-blue text-primary-foreground" : "bg-accent text-primary"
            )}
          >
            Book
          </div>
        </div>
      ))}
    </div>
  </div>
);

const SitePhotos = () => (
  <div>
    <div className="text-[10px] font-semibold uppercase tracking-wider text-primary">Gallery</div>
    <h4 className="font-display text-lg font-bold text-navy">See our work</h4>
    <div className="mt-3 grid grid-cols-3 gap-2">
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          className={cn(
            "aspect-square rounded-lg shadow-sm ring-1 ring-border",
            i % 3 === 0
              ? "bg-gradient-to-br from-accent to-secondary"
              : i % 3 === 1
              ? "bg-gradient-to-br from-secondary to-accent"
              : "bg-gradient-to-br from-techblue/20 to-accent"
          )}
        />
      ))}
    </div>
  </div>
);

const SiteAbout = () => (
  <div>
    <div className="text-[10px] font-semibold uppercase tracking-wider text-primary">About Us</div>
    <h4 className="font-display text-lg font-bold text-navy">Family-owned. Trusted since 2009.</h4>
    <div className="mt-3 flex gap-3">
      <div className="h-24 w-24 shrink-0 rounded-xl bg-gradient-to-br from-secondary to-accent ring-1 ring-border" />
      <div className="space-y-2 pt-1">
        <div className="h-2 w-full rounded bg-muted" />
        <div className="h-2 w-11/12 rounded bg-muted" />
        <div className="h-2 w-4/5 rounded bg-muted" />
        <div className="h-2 w-2/3 rounded bg-muted" />
        <div className="mt-1 inline-flex rounded-full bg-accent px-2.5 py-1 text-[9px] font-semibold text-primary">
          500+ happy homes
        </div>
      </div>
    </div>
  </div>
);

const SiteInfo = () => (
  <div>
    <div className="text-[10px] font-semibold uppercase tracking-wider text-primary">Visit / Contact</div>
    <h4 className="font-display text-lg font-bold text-navy">Get in touch</h4>
    <div className="mt-3 space-y-2">
      {[
        [Phone, "(209) 555-0142"],
        [Clock, "Mon–Sat · 8AM–6PM"],
        [MapPin, "Central Valley, CA"],
      ].map(([Icon, val]) => (
        <div key={val} className="flex items-center gap-3 rounded-xl border border-border bg-card p-3 shadow-sm">
          <span className="grid h-8 w-8 place-items-center rounded-lg bg-accent text-primary">
            <Icon className="h-4 w-4" />
          </span>
          <span className="text-[12px] font-medium text-navy">{val}</span>
        </div>
      ))}
    </div>
  </div>
);

const SiteRequest = () => (
  <div>
    <div className="text-[10px] font-semibold uppercase tracking-wider text-primary">Request Service</div>
    <h4 className="font-display text-lg font-bold text-navy">Book your free quote</h4>
    <div className="mt-3 space-y-2 rounded-xl border border-border bg-card p-3 shadow-sm">
      <div className="h-8 rounded-lg border border-border bg-secondary/60" />
      <div className="h-8 rounded-lg border border-border bg-secondary/60" />
      <div className="grid grid-cols-2 gap-2">
        <div className="h-8 rounded-lg border border-border bg-secondary/60" />
        <div className="h-8 rounded-lg border border-border bg-secondary/60" />
      </div>
      <div className="flex items-center justify-center gap-1.5 rounded-lg bg-gradient-blue py-2 text-[11px] font-semibold text-primary-foreground">
        <Send className="h-3.5 w-3.5" /> Request Quote
      </div>
    </div>
  </div>
);

const PANELS = {
  services: { dash: DashServices, site: SiteServices },
  pricing: { dash: DashPricing, site: SitePricing },
  photos: { dash: DashPhotos, site: SitePhotos },
  about: { dash: DashAbout, site: SiteAbout },
  info: { dash: DashInfo, site: SiteInfo },
  request: { dash: DashRequest, site: SiteRequest },
};

export const ProductShowcase = () => {
  const [active, setActive] = useState("services");
  const Dash = PANELS[active].dash;
  const Site = PANELS[active].site;

  return (
    <section id="preview" className="relative overflow-hidden bg-navy-deep py-20 lg:py-28">
      <div className="absolute inset-0 grid-pattern opacity-30" />
      <div className="absolute left-1/2 top-0 h-72 w-72 -translate-x-1/2 rounded-full bg-techblue/20 blur-3xl" />

      <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
        <Reveal className="mx-auto max-w-3xl text-center">
          <span className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3.5 py-1.5 text-xs font-semibold uppercase tracking-wider text-techblue-glow">
            <span className="flex h-1.5 w-1.5 rounded-full bg-techblue-glow animate-pulse-glow" />
            Platform Preview
          </span>
          <h2 className="font-display text-3xl font-bold leading-tight tracking-tight text-navy-foreground sm:text-4xl lg:text-5xl">
            See the Platform in Action
          </h2>
          <p className="mt-5 text-base text-navy-muted md:text-lg">
            Click any tab to explore the Anchor management platform — and watch your website
            update beside it. This is how simple managing your website can be.
          </p>
        </Reveal>

        {/* Tabs */}
        <Reveal delay={0.1} className="mt-12">
          <div className="mx-auto flex max-w-3xl flex-wrap items-center justify-center gap-2">
            {TABS.map((t) => {
              const Icon = t.icon;
              const isActive = active === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => setActive(t.id)}
                  aria-pressed={isActive}
                  className={cn(
                    "inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-all duration-300",
                    isActive
                      ? "bg-gradient-blue text-primary-foreground shadow-blue"
                      : "border border-white/15 bg-white/5 text-navy-muted hover:bg-white/10 hover:text-navy-foreground"
                  )}
                >
                  <Icon className="h-4 w-4" />
                  {t.label}
                </button>
              );
            })}
          </div>
        </Reveal>

        {/* Split view */}
        <Reveal delay={0.15} className="mt-12">
          <div className="grid grid-cols-1 items-center gap-6 lg:grid-cols-2 lg:gap-8">
            {/* Dashboard */}
            <div className="relative">
              <div className="absolute -inset-2 rounded-3xl bg-gradient-blue opacity-10 blur-2xl" />
              <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-navy shadow-elegant ring-1 ring-white/10 animate-float-subtle">
                <div className="flex items-center gap-2 border-b border-white/10 bg-navy-deep px-4 py-2.5">
                  <div className="flex gap-1.5">
                    <span className="h-2.5 w-2.5 rounded-full bg-white/20" />
                    <span className="h-2.5 w-2.5 rounded-full bg-white/20" />
                    <span className="h-2.5 w-2.5 rounded-full bg-white/20" />
                  </div>
                  <span className="ml-2 text-[10px] font-medium text-navy-muted">
                    Anchor Dashboard
                  </span>
                  <span className="ml-auto inline-flex items-center gap-1 text-[10px] text-techblue-glow">
                    <span className="h-1.5 w-1.5 rounded-full bg-techblue-glow" /> Saved
                  </span>
                </div>
                <AnimatePresence mode="wait">
                  <motion.div key={active} {...dashMotion}>
                    <Dash />
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>

            {/* Website */}
            <div className="relative">
              <div className="mb-3 flex items-center justify-center gap-2 text-[11px] font-medium text-navy-muted lg:justify-start">
                <ArrowUpRight className="h-3.5 w-3.5 text-techblue-glow" />
                Your live website updates instantly
              </div>
              <BrowserFrame url="sparkleclean.com" theme="light" className="shadow-elegant">
                <AnimatePresence mode="wait">
                  <motion.div key={active} {...siteMotion}>
                    <SiteChrome>
                      <Site />
                    </SiteChrome>
                  </motion.div>
                </AnimatePresence>
              </BrowserFrame>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
